/**
 * @since 2026-05-10
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import React from "react";
import { Box, LinearProgress } from "@mui/material";

const DaysSinceLastUpdatePanel = ({ detail }) => {
  if (!detail) return null;
  const {
    severity,
    daysSince,
    firstDate,
    lastDate,
    warnThreshold,
    errorThreshold,
    medianGap,
    barsForGap,
    perSourceLastDates,
  } = detail;
  const sourceEntries = perSourceLastDates && typeof perSourceLastDates === "object"
    ? Object.entries(perSourceLastDates)
        .filter(([, d]) => Boolean(d))
        .sort((a, b) => b[1].localeCompare(a[1]))
    : [];

  const hasDays = typeof daysSince === "number";
  const warn = typeof warnThreshold === "number" ? warnThreshold : 1;
  const error = typeof errorThreshold === "number" ? errorThreshold : 5;
  const ratio = hasDays && error > 0 ? Math.min(100, (daysSince / error) * 100) : 0;
  const color = severity === "error" ? "error" : severity === "warn" ? "warning" : "success";

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 1 }}>
        <Box sx={{ fontSize: 22, fontWeight: 600 }}>
          {hasDays ? `${daysSince} business day${daysSince === 1 ? "" : "s"}` : "No data"}
        </Box>
        <Box sx={{ fontSize: 13, opacity: 0.7 }}>
          warn &gt; {warn} · error &gt; {error}
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={ratio}
        color={color}
        sx={{ height: 8, borderRadius: 1, mb: 2 }}
      />
      <Box sx={{ display: "flex", gap: 4, fontSize: 13, flexWrap: "wrap" }}>
        <Box>
          <Box sx={{ opacity: 0.7 }}>First bar</Box>
          <Box>{firstDate ? firstDate.slice(0, 10) : "—"}</Box>
        </Box>
        <Box>
          <Box sx={{ opacity: 0.7 }}>Last bar</Box>
          <Box>{lastDate ? lastDate.slice(0, 10) : "—"}</Box>
        </Box>
        <Box>
          <Box sx={{ opacity: 0.7 }}>Median gap</Box>
          <Box>
            {typeof medianGap === "number" ? `${medianGap.toFixed(2)} BD` : "—"}
            {typeof barsForGap === "number" && barsForGap > 0 ? ` (n=${barsForGap})` : ""}
          </Box>
        </Box>
      </Box>
      {sourceEntries.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ fontSize: 13, fontWeight: 600, opacity: 0.85, mb: 0.5 }}>
            Last data point per source
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.25, fontSize: 13 }}>
            {sourceEntries.map(([source, date]) => (
              <React.Fragment key={source}>
                <Box sx={{ opacity: 0.85 }}>{source}</Box>
                <Box>{date}</Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DaysSinceLastUpdatePanel;
